import React, { useState, useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import connect from '../../utils/connect';
import { Context } from './OnboardingContext';
import sportsApi from '../../utils/sportsApi';
import combineActions from '../../utils/combineActions';

import deactivateSponsor from '../../actions/settings/deactivateSponsor';
import dissociateSponsor from '../../actions/settings/dissociateSponsor';
import substringMatch from '../../utils/substringMatch';

export const SponsorContext = React.createContext({});

const SponsorProvider = ({
    client,
    children,
    deactivateSponsor,
    dissociateSponsor,
}) => {
    const { rhId, structureLocked, setIsRemovingSP } = useContext(Context);

    const [searchTerm, setSearchTerm] = useState('');
    const [updateFilters, setUpdateFilters] = useState(false);
    const [sponsors, setSponsors] = useState([]);
    const [showSponsorSelector, setShowSponsorSelector] = useState(false);
    const [toAdd, setToAdd] = useState([]);
    const [toRemove, setToRemove] = useState([]);
    const [selected, setSelected] = useState([]);
    const [potentials, setPotentials] = useState([]);
    const [toEdit, setToEdit] = useState(null);

    const [onCustomTab, setCustomTab] = useState(false);
    const [newRequests, setNewRequests] = useState([]);
    const [requested, setRequested] = useState([]);

    const matchingSelected = useMemo(
        () =>
            selected
                .filter(sp => substringMatch(searchTerm, sp.name))
                .sort((a, b) => a.name.localeCompare(b.name)),
        [selected, searchTerm],
    );

    const removeSponsor = useCallback(
        sp => async () => {
            try {
                if (structureLocked) return;
                const confirmation = confirm(
                    `Warning: You are about to delete or deactivate ${sp.name} from your configuration`,
                );

                if (!confirmation) return;
                setIsRemovingSP(true);

                const deletedSponsor = await sportsApi
                    .url(
                        `/clients/${client.id}/rightsholders/${rhId}/sponsors/${sp.id}`,
                    )
                    .delete()
                    .json();

                const shouldDeletePermenantly = !(
                    deletedSponsor && deletedSponsor.clientRemoved
                );

                if (shouldDeletePermenantly) {
                    await dissociateSponsor({ rhId, sponsorId: sp.id });
                    setUpdateFilters(true);

                    const newSponsors = sponsors.filter(s => s.id !== sp.id);
                    setSponsors(newSponsors);

                    setPotentials([...potentials, sp]);
                    const newSelected = selected
                        .reduce((acc, s) => {
                            if (s.id !== sp.id) {
                                acc.push(s);
                            }
                            return acc;
                        }, [])
                        .sort((a, b) => a.name.localeCompare(b.name));
                    setSelected(newSelected);
                } else {
                    await deactivateSponsor({ rhId, sponsor: deletedSponsor });
                    setUpdateFilters(true);

                    const newSponsors = sponsors.map(s =>
                        s.id === sp.id
                            ? {
                                  ...s,
                                  ...deletedSponsor,
                              }
                            : s,
                    );

                    setSponsors(newSponsors);

                    const newSelected = selected
                        .map(s => {
                            if (s.id === sp.id) {
                                return { ...s, ...deletedSponsor };
                            }
                            return s;
                        })
                        .sort((a, b) => a.name.localeCompare(b.name));
                    setSelected(newSelected);
                }
                setIsRemovingSP(false);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.log(err);
                setIsRemovingSP(false);
            }
        },
        [structureLocked, client, rhId, sponsors, potentials, selected],
    );

    const providerValue = {
        searchTerm,
        setSearchTerm,
        updateFilters,
        setUpdateFilters,
        sponsors,
        setSponsors,
        showSponsorSelector,
        setShowSponsorSelector,
        toAdd,
        setToAdd,
        toRemove,
        setToRemove,
        selected,
        setSelected,
        potentials,
        setPotentials,
        matchingSelected,
        toEdit,
        setToEdit,
        onCustomTab,
        setCustomTab,
        newRequests,
        setNewRequests,
        requested,
        setRequested,
        removeSponsor,
    };

    return (
        <SponsorContext.Provider value={providerValue}>
            {children}
        </SponsorContext.Provider>
    );
};

SponsorProvider.propTypes = {
    children: PropTypes.node.isRequired,
    client: PropTypes.object.isRequired,
    deactivateSponsor: PropTypes.func.isRequired,
    dissociateSponsor: PropTypes.func.isRequired,
};

export default connect(
    ['client'],
    combineActions({
        deactivateSponsor,
        dissociateSponsor,
    }),
)(SponsorProvider);
